/**
 * News comment configuration (Sprint 3a).
 * Seeded NPC comments are derived deterministically from the news instanceId
 * (never saved); only the player's comments + NPC replies live in state.
 * Meme texts are i18n keys: cmt_up_1..N / cmt_down_1..N, replies npc_reply_1..N.
 */
import { drawMany, type BagStore } from '../shuffleBag.js';

/** Crypto-style handles - intentionally locale-independent (community usernames) */
export const NPC_NICKS = [
  'HODL_KING', 'moon_hunter88', 'DiamondPaws', 'ser_pump', 'rekt_again',
  'ChartWizard', 'fomo_grandma', 'WhaleWatcher_0', 'bagholder_lee', 'Cptn_Candle',
  'zero_to_moon', 'PanicSeller', 'satoshi_nephew', 'dip_buyer_777', 'LamboSoon',
  'rug_survivor', 'Insider_Maybe', 'coin_hamster', 'MaxLeverage', 'exit_liquidity',
  'jonber_3yr', 'mooltagi_master', 'chart_gazua', 'seed_melting', 'basement_dweller',
  'close_the_window', 'chicken_tonight', 'my_avg_is_pain', 'ser_wen_lambo', 'ape_no_brain',
  'rug_museum', 'green_candle_pls', 'liquidation_ptsd', 'coin_monk', 'still_holding_lol',
  'exit_liquidity_02', 'buy_high_sell_low', 'gods_of_cutloss', 'ramyeon_diet', 'ghost_of_luna',
  'FOMO_alarm', 'copium_dealer', 'thin_ice_trader', 'candlestick_shaman', 'hopium_addict',
  'not_financial_ad', 'wen_moon_fren', 'zero_liquidity',
] as const;

export const COMMENT_CONFIG = {
  /** seeded NPC comments per news item */
  minSeeded: 3,
  maxSeeded: 5,
  /** i18n meme pool size per direction (cmt_up_N / cmt_down_N) => 200 total */
  poolSize: 100,
  /** i18n generic NPC reply pool size (npc_reply_N) */
  replyPool: 16,
  /** NPC reacts to the player's comment after 5~15s */
  replyDelayMin: 5_000,
  replyDelayMax: 15_000,
  replyCountMin: 1,
  replyCountMax: 2,
  /** max chars for the player's one-line comment */
  maxLength: 80,
  /** hard cap on stored player comments (feed trim is the primary bound) */
  maxStoredComments: 50,
} as const;

/**
 * Static stance choices the player can tap (no free typing needed).
 * Each is an i18n key (stance_N); reply pools are biased by stance mood so the
 * NPC banter feels like it answers the player. Pure flavor - no market impact.
 */
export interface StanceChoice {
  /** i18n key for the button + posted comment text */
  key: string;
  /** mood drives which reply pool the NPCs draw from */
  mood: 'bullish' | 'bearish' | 'neutral';
}

export const STANCE_CHOICES: readonly StanceChoice[] = [
  { key: 'stance_moon', mood: 'bullish' },   // "가즈아"
  { key: 'stance_cut', mood: 'bearish' },    // "손절각"
  { key: 'stance_watch', mood: 'neutral' },  // "관망"
  { key: 'stance_rekt', mood: 'bearish' },   // "나만 물렸냐"
] as const;

/** reply pool key prefix for a stance mood (npc_reply_{mood}_N, 24 each) */
export const REPLY_POOL_BY_MOOD: Record<StanceChoice['mood'], string> = {
  bullish: 'bull',
  bearish: 'bear',
  neutral: 'neut',
};

/** number of NPC reply variants per mood pool (npc_reply_{mood}_1..N) */
export const REPLY_POOL_SIZE = 24;

export function getStance(stanceIdx: number): StanceChoice | undefined {
  return STANCE_CHOICES[stanceIdx];
}

/** FNV-1a string hash - stable seed source for a news instanceId */
export function hashSeed(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** mulberry32 PRNG - deterministic sequence from a numeric seed */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface SeededComment {
  /** index into NPC_NICKS */
  nickIdx: number;
  /** index into the direction pool (0-based; i18n key = cmt_{dir}_{textIdx+1}) */
  textIdx: number;
  /** minutes-ago offset for display flavor (0..30) */
  agoMin: number;
}

/**
 * Deterministic 3~5 NPC comments for a news item. Same instanceId always
 * yields the same comments (reproducible across sessions - nothing saved).
 * The caller picks the pool by news direction (up/down).
 */
export function seedComments(instanceId: string): SeededComment[] {
  const rand = mulberry32(hashSeed(instanceId));
  const count = COMMENT_CONFIG.minSeeded
    + Math.floor(rand() * (COMMENT_CONFIG.maxSeeded - COMMENT_CONFIG.minSeeded + 1));

  const nicks = pickDistinct(rand, NPC_NICKS.length, count);
  const texts = pickDistinct(rand, COMMENT_CONFIG.poolSize, count);
  const comments: SeededComment[] = [];
  for (let i = 0; i < count; i++) {
    comments.push({ nickIdx: nicks[i], textIdx: texts[i], agoMin: Math.floor(rand() * 31) });
  }
  return comments;
}

function pickDistinct(rand: () => number, poolSize: number, count: number): number[] {
  const picked: number[] = [];
  while (picked.length < Math.min(count, poolSize)) {
    const idx = Math.floor(rand() * poolSize);
    if (!picked.includes(idx)) picked.push(idx);
  }
  return picked;
}

/**
 * Draw 3~5 seeded NPC comments for a freshly published news item using the
 * per-direction shuffle bag, so memes don't repeat across the feed until the
 * whole pool is exhausted. Returns the comments + the advanced bag store; the
 * caller stores the comments on the feed item (stateful, unlike seedComments).
 */
export function drawSeededComments(
  bags: BagStore, direction: 1 | -1, now: number
): { comments: SeededComment[]; bags: BagStore } {
  void now;
  const count = COMMENT_CONFIG.minSeeded
    + Math.floor(Math.random() * (COMMENT_CONFIG.maxSeeded - COMMENT_CONFIG.minSeeded + 1));
  const textKey = direction === 1 ? 'cmt_up' : 'cmt_down';
  const textRes = drawMany(bags, textKey, COMMENT_CONFIG.poolSize, count);
  const nickRes = drawMany(textRes.bags, 'cmt_nick', NPC_NICKS.length, count);
  const comments: SeededComment[] = [];
  for (let i = 0; i < count; i++) {
    comments.push({
      nickIdx: nickRes.indices[i],
      textIdx: textRes.indices[i],
      agoMin: Math.floor(Math.random() * 31),
    });
  }
  return { comments, bags: nickRes.bags };
}
